import type { QueryObserverResult, RefetchOptions } from '@tanstack/vue-query';
import type { User, UserDTO } from '@/data/User';
import { httpGet } from '@/api/http';
import { V2_USERS } from '@/constants/endpoint';
import { computed, type Ref } from 'vue';
import { useUsersAvailability } from '@/api/useUsersAvailability';
import { useAuthenticatedQuery } from '@/utils/query';
import type { Presence, StatusType } from '@/data/UserAvailability';
import type { Uuid } from '@/data/Types';

export const USERS_QUERY_KEY = 'users';

export interface ExtendedUser extends User {
  userStatus?: StatusType;
  presence: Presence | null;
}

export interface MapingEntry {
  key: Uuid;
  value: ExtendedUser;
}

// need to explicitly type due to issue
// https://github.com/TanStack/query/issues/6318
export const usersQuery = {
  queryKey: [USERS_QUERY_KEY],
  queryFn: () => httpGet<UserDTO[]>(V2_USERS).then((usersDto) => usersDto.map(mapToUser)),
  refetchInterval: 600_000,
  refetchOnMount: false,
  gcTime: Infinity,
};

export const useUsers = (
  mapping = byUserUuidMapping
): {
  data: Ref<Map<string, ExtendedUser> | undefined>;
  isLoading: Ref<boolean>;
  refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<User[], unknown>>;
} => {
  const { data: _users, isLoading, refetch } = useAuthenticatedQuery(usersQuery);
  const { data: usersAvailability } = useUsersAvailability();

  const data = computed(() => {
    const map = new Map<Uuid, ExtendedUser>();
    const users = _users.value ?? ([] as User[]);
    users.forEach((user) => {
      const mapEntry = mapping(toExtended(user));
      if (mapEntry) map.set(mapEntry.key, mapEntry.value);
    });
    return map;
  });

  const toExtended = (user: User): ExtendedUser => {
    const userAvailability = usersAvailability.value?.[user.uuid];
    const presence = user.extension
      ? ({
          inCall: userAvailability?.inCall ?? false,
          present: userAvailability?.present ?? false,
          callState: userAvailability?.callState ?? null,
        } as Presence)
      : null;

    return {
      ...user,
      userStatus: userAvailability?.statusType,
      presence,
    };
  };

  return {
    data,
    isLoading,
    refetch,
  };
};

export const byUserUuidMapping = (extendedUser: ExtendedUser): MapingEntry | undefined => {
  if (extendedUser.uuid) {
    return {
      key: extendedUser.uuid,
      value: extendedUser,
    };
  }
};

export const byContactUuidMapping = (extendedUser: ExtendedUser): MapingEntry | undefined => {
  if (extendedUser.contactUuid) {
    return {
      key: extendedUser.contactUuid,
      value: extendedUser,
    };
  }
};

export const mapToUser = (user: UserDTO): User => {
  return {
    uuid: user.uuid,
    contactUuid: user.contactUuid,
    username: user.username,
    permissionGroups: user.groups,
    agentUuid: user.agentUuid,
    extension: user.extension,
    name: user.username,
  };
};
